/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React, { ReactElement } from 'react';
import { observer } from 'mobx-react';
import ModuleEditingViewStyles from './ModuleEditingViewStyles';
import { Box, CircularProgress } from '@material-ui/core';
import { ModuleEditingViewModel } from './ModuleEditingViewModel';
import { useViewModel } from '../../../../hooks/useViewModel';
import CourseHeaderView from '../CourseHeader/CourseHeaderView';
import CourseTableView from '../CourseTable/CourseTableView';
import { ModuleDetailView } from './DetailView/ModuleDetailView';
import AddModuleDialog from './AddModuleDialog/AddModuleDialogView';
import { Alert } from '@material-ui/lab';
import { FormattedMessage } from 'react-intl';
import { ModulePackage } from '../../../../types/course/ModulePackage';

const ModuleEditingView = observer((): ReactElement => {
	const classes = ModuleEditingViewStyles();
	const viewModel = useViewModel(ModuleEditingViewModel);
	const modulePackage = viewModel.getModulePackage();

	const closeDialog = (): void => {
		viewModel.setModulePackage({
			id: '',
			name: '',
			description: '',
			image: null,
		});
	};

	return (
		<>
			<Box className={classes.contentContainer}>
				{!viewModel.canCourseBeEdited() && (
					<Alert severity='warning' className={classes.warning}>
						<FormattedMessage id={'courses.not_editable'} />
					</Alert>
				)}
				<CourseHeaderView
					buttonFunction={() => viewModel.openAddModuleDialog()}
					buttonDisabled={!viewModel.getDisableButton()}
					buttonText={'modules.add_module'}
					title={'modules.module_editing.title'}
				/>
				{viewModel.isLoading() ? (
					<Box className={classes.loadingContainer}>
						<CircularProgress className={classes.loading} />
					</Box>
				) : (
					<CourseTableView
						rows={viewModel.getCourseModulePackages()}
						columns={viewModel.columns}
						menuItems={viewModel.getMenuItems()}
						emptyTableMessage={'modules.module_editing.table.empty_table'}
						setRow={(row: ModulePackage) => viewModel.setModulePackage(row)}
					/>
				)}
			</Box>
			{viewModel.isAddModuleDialogOpen() && <AddModuleDialog />}
			<ModuleDetailView
				open={viewModel.isDetailModulePackageSet()}
				moduleName={modulePackage.name}
				moduleDescription={modulePackage.description}
				moduleImage={modulePackage.image}
				closeDialog={closeDialog}
				removeFromCourse={() => {
					viewModel.removeModulePackage(modulePackage.id);
					closeDialog();
				}}
				hasCourseStarted={viewModel.hasCourseStarted()}
				canCourseBeEdited={viewModel.canCourseBeEdited()}
			/>
		</>
	);
});

export default ModuleEditingView;
